import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { useLang } from "../../lib/i18n";

const SLIDES = [
  {
    name: "Alaska",
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_bridal-atelier-luxe/artifacts/9rjsv63q_alaska-carrie_shoes-0s3a3005_1.webp",
  },
  {
    name: "Alba",
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_bridal-atelier-luxe/artifacts/tf17shdq_alba-avalia-bridal-shoes-2.webp",
  },
  {
    name: "Demi",
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_bridal-atelier-luxe/artifacts/qxgebvb2_demi-avalia-bridal-shoes-2.webp",
  },
  {
    name: "Cindy",
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_bridal-atelier-luxe/artifacts/5m1p8ird_avalia-bridal-shoes-cindy-glitter-_3_.webp",
  },
  {
    name: "Capri",
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_bridal-atelier-luxe/artifacts/y7owq8e8_capri-avalia-bridal-shoes-2.webp",
  },
];

const ShoeCarousel = () => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);
  const prev = useCallback(
    () => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length),
    []
  );

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 3500);
    return () => clearInterval(t);
  }, [paused, next]);

  return (
    <div
      data-testid="shoes-carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="group relative w-full overflow-hidden rounded-2xl md:rounded-none bg-[#0e0e0e] ring-1 ring-white/10"
    >
      <motion.div
        className="flex h-[420px] md:h-[600px] cursor-grab active:cursor-grabbing"
        animate={{ x: `-${idx * 100}%` }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.12}
        onDragStart={() => setPaused(true)}
        onDragEnd={(_, info) => {
          if (info.offset.x < -60) next();
          else if (info.offset.x > 60) prev();
        }}
      >
        {SLIDES.map((s, i) => (
          <div
            key={s.name}
            data-testid={`shoes-carousel-slide-${i}`}
            className="w-full h-full shrink-0 flex items-center justify-center p-6 md:p-10"
          >
            <img
              src={s.src}
              alt={`Scarpe da sposa — ${s.name}`}
              draggable={false}
              loading={i === 0 ? "eager" : "lazy"}
              className="max-h-full max-w-full object-contain select-none pointer-events-none"
            />
          </div>
        ))}
      </motion.div>

      {/* Model name badge */}
      <span className="absolute top-5 left-5 font-serif-display italic text-lg text-gold bg-[#050505]/60 backdrop-blur px-4 py-1.5">
        {SLIDES[idx].name}
      </span>

      {/* Arrows */}
      <button
        data-testid="shoes-carousel-prev"
        onClick={prev}
        aria-label="Precedente"
        className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-[#050505]/35 backdrop-blur text-white/70 hover:border-gold hover:text-gold transition-colors duration-300"
      >
        <ChevronLeft size={18} strokeWidth={1.5} />
      </button>
      <button
        data-testid="shoes-carousel-next"
        onClick={next}
        aria-label="Successiva"
        className="absolute right-3 md:right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-[#050505]/35 backdrop-blur text-white/70 hover:border-gold hover:text-gold transition-colors duration-300"
      >
        <ChevronRight size={18} strokeWidth={1.5} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {SLIDES.map((s, i) => (
          <button
            key={s.name}
            data-testid={`shoes-carousel-dot-${i}`}
            onClick={() => setIdx(i)}
            aria-label={s.name}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === idx ? "w-7 bg-gold" : "w-1.5 bg-white/35 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export const Shoes = () => {
  const { tr } = useLang();
  return (
    <section
      id="scarpe"
      data-testid="shoes-section"
      className="relative bg-ad-black py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 items-center gap-12 md:gap-20">
          {/* Carousel */}
          <Reveal>
            <ShoeCarousel />
          </Reveal>

          {/* Text */}
          <Reveal delay={0.15}>
            <Overline>{tr("Il Tocco Finale", "The Finishing Touch")}</Overline>
            <h2 className="mt-6 font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white">
              {tr("Scarpe da Sposa", "Bridal Shoes")}
              <span className="block italic text-gold mt-1">{tr("per completare il look", "to complete the look")}</span>
            </h2>
            <p className="mt-8 font-sans-ui font-light text-white/70 leading-relaxed max-w-md">
              {tr(
                "Oltre agli abiti, in Atelier troverai una raffinata selezione di scarpe da sposa. Perché l'eleganza vive anche nei dettagli, dal primo passo verso l'altare.",
                "Beyond the gowns, the Atelier offers a refined selection of bridal shoes. Because elegance lives in the details, from the very first step towards the altar."
              )}
            </p>
            <p className="mt-5 font-sans-ui font-light text-white/55 leading-relaxed max-w-md">
              {tr(
                "Ti aiuteremo a scegliere il modello perfetto, in armonia con il tuo abito e con il tuo stile.",
                "We will help you choose the perfect pair, in harmony with your dress and your style."
              )}
            </p>
            <div className="hairline w-24 mt-10" />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
